/**
 * QUIL — Shared prose-text helpers.
 * Word counting and plain-text extraction, used by the Dashboard (counts stored
 * prose) and the ProseEditor (counts the live editor text). Extracted here so the
 * logic has a single source of truth and is unit-testable.
 */

type ProseNode = { type?: string; text?: string; attrs?: Record<string, unknown>; content?: ProseNode[] }

/** Recursively extract plain text from a TipTap doc node. */
function extractText(node: ProseNode): string {
  if (node.text) return node.text
  if (node.content) return node.content.map(extractText).join(' ')
  return ''
}

/** Extract plain text from a stored prose string (TipTap JSON, or plain text).
 * @internal Exported for its unit tests; production reaches it only from inside this module.
 */
export function extractProseText(prose: string): string {
  if (!prose) return ''
  try {
    return extractText(JSON.parse(prose) as ProseNode)
  } catch {
    // Not JSON — treat as plain text.
    return prose
  }
}

/** Count words in an already-extracted plain-text string. */
export function countWords(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).filter(Boolean).length : 0
}

/** Count words in a stored prose string (TipTap JSON, or plain text). */
export function countProseWords(prose: string): number {
  return countWords(extractProseText(prose))
}

/**
 * Per-scene word counts for a chapter (scene navigator, §7). Scenes are marked in
 * the prose by `sceneAnchor` nodes carrying a `sceneId`; the text BEFORE the first
 * anchor belongs to the chapter's first scene (which never has an anchor — it is
 * the document start), and the text after each anchor belongs to that anchor's
 * scene, up to the next anchor. `firstSceneId` is the id of the first scene chip
 * (scenes[0]); pass null if the chapter has no scenes. Scenes that exist but have
 * no anchor placed yet (unstarted) simply never appear in the map → treat as 0.
 * Pure over the stored prose so it's unit-testable and reused off the save path.
 */
export function sceneWordCounts(prose: string, firstSceneId: string | null): Record<string, number> {
  const counts: Record<string, number> = {}
  if (!prose) return counts
  let doc: ProseNode
  try {
    doc = JSON.parse(prose) as ProseNode
  } catch {
    // Not JSON — plain text all belongs to the first scene.
    if (firstSceneId) counts[firstSceneId] = countWords(prose)
    return counts
  }
  let current = firstSceneId
  const buffers: Record<string, string> = {}
  const walk = (node: ProseNode): void => {
    if (node.type === 'sceneAnchor') {
      const id = node.attrs?.sceneId
      if (typeof id === 'string') current = id
      return
    }
    if (node.text) {
      if (current != null) buffers[current] = `${buffers[current] ?? ''} ${node.text}`
      return
    }
    node.content?.forEach(walk)
  }
  walk(doc)
  for (const [id, buffer] of Object.entries(buffers)) counts[id] = countWords(buffer)
  return counts
}

/** Human-readable word count, e.g. "1.2k words" / "1 word" / "0 words". */
export function formatWordCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k words`
  return `${n} word${n === 1 ? '' : 's'}`
}

/**
 * Format a build timestamp (ISO string) in New Zealand time with its NZST/NZDT
 * abbreviation, e.g. "21 Jul 2026, 10:42 pm NZST" — the form Kamrin reads the
 * deploy version in (Settings "Version" row + the update prompt's incoming build).
 * Falls back to the raw string if the value can't be parsed/formatted.
 */
export function formatBuildTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-NZ', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZone: 'Pacific/Auckland', timeZoneName: 'short',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

/**
 * Compact relative-time label ("just now", "5m ago", "3h ago", "2d ago"), falling
 * back to a locale date beyond a week. Shared by the draft-safety surfaces
 * (Versions panel, recovery shelf, graveyard) where finer granularity than the
 * Dashboard's day-level `relativeDate` matters. `now` is injectable for tests.
 */
export function relativeTime(ms: number, now: number = Date.now()): string {
  const s = Math.max(0, Math.round((now - ms) / 1000))
  if (s < 45) return 'just now'
  const m = Math.round(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.round(h / 24)
  if (d < 7) return `${d}d ago`
  return new Date(ms).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * How long a work has been quiet, in plain words.
 *
 * DESIGN.md move 07 — "time is the subject, not a metric". The reference is about
 * elapsed time and how differently it reads at different lifespans, so Quil states
 * the *length of a silence* rather than counting days at you. No "ago", no
 * "overdue", no numbers past a week — a work resting since the autumn should read
 * as "a season", not "94 days".
 *
 * `now` is injectable for tests.
 */
export function silenceLength(ms: number, now: number = Date.now()): string {
  const day = 86_400_000
  const diff = Math.max(0, now - ms)
  const days = Math.floor(diff / day)

  if (days === 0) return 'today'
  if (days === 1) return 'yesterday'

  const WORDS = ['', '', 'two', 'three', 'four', 'five', 'six']
  if (days < 7) return `${WORDS[days]} days`

  const weeks = Math.round(days / 7)
  if (days < 28) return weeks <= 1 ? 'a week' : `${WORDS[weeks] ?? weeks} weeks`
  if (days < 60) return 'a month'
  // the Frieren beat: past a couple of months, stop counting and name the span
  if (days < 150) return 'a season'
  if (days < 300) return 'most of a year'

  const years = Math.round(days / 365)
  return years <= 1 ? 'a year' : `${WORDS[years] ?? years} years`
}

/**
 * Split a chapter's number-label from its custom title, de-duplicating the
 * default "Chapter N" name so UIs never render "Chapter 3 · Chapter 3".
 * `chapterNumber` is the 1-based position in the work's ordered chapter list.
 * `custom` is null when the title is empty or is just the "Chapter N" default,
 * so callers show the number alone; otherwise render both with their own
 * separator (WS uses " · ", export uses " — ").
 */
export function chapterLabel(
  title: string | undefined,
  chapterNumber: number,
): { label: string; custom: string | null } {
  const t = (title ?? '').trim()
  const isDefault = t === '' || /^chapter\s+\d+$/i.test(t)
  return { label: `Chapter ${chapterNumber}`, custom: isDefault ? null : t }
}
